import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { 
  CreditCard, 
  Building2, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  Copy, 
  Download,
  Truck,
  ShieldCheck,
  FileText,
  Loader2,
  Wallet,
  AlertTriangle,
  X,
  Clock,
  Package,
  Percent,
  ShoppingCart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  cartEligible as paypalCartEligible,
  PAYPAL_INSTALLMENT_DISCLAIMER,
  PAYPAL_GROSS_SURCHARGE,
} from '../utils/paypalEligibility';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

// AGB Warning Modal Component
const AGBWarningModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Bestätigung erforderlich</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-6">
          <p className="text-gray-600 text-lg leading-relaxed">
            Bitte bestätigen Sie <strong>AGB</strong>, <strong>Datenschutz</strong> und <strong>Widerrufsbelehrung</strong>, um fortzufahren.
          </p>
        </div>
        
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <ul className="space-y-2 text-sm text-amber-800">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              AGB akzeptieren
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Datenschutzerklärung akzeptieren
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Widerrufsbelehrung akzeptieren
            </li>
          </ul>
        </div>
        
        <Button 
          onClick={onClose}
          className="w-full bg-red-600 hover:bg-red-700 py-3 text-lg"
        >
          Verstanden
        </Button>
      </div>
    </div>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    cart, 
    getCartTotal, 
    getTaxAmount, 
    getGrandTotal, 
    clearCart, 
    setCart,
    hasPreorderItems,
    isPreorderCart,
    getCartOriginalValue,
    getTotalDiscount,
    getTotalRemainingAmount,
    getCartFullValue,
    PREORDER_DISCOUNT,
    PREORDER_DEPOSIT
  } = useCart();
  const { user, isAuthenticated } = useAuth();
  
  // Check if returning from payment gateway
  const isMollieReturnInitial = searchParams.get('mollie_success') === 'true' && searchParams.get('order_id');
  const isPayPalReturnInitial = searchParams.get('paypal_success') === 'true' && searchParams.get('order_id');
  const isPaymentRetry = searchParams.get('retry') === 'true';
  const paymentFailed = searchParams.get('payment_failed') === 'true';
  
  const [step, setStep] = useState(1); // 1: Address, 2: Payment, 3: Confirmation
  const [paymentMethod, setPaymentMethod] = useState('mollie_creditcard'); // 'mollie_creditcard', 'mollie_klarna', 'mollie_banktransfer', 'mollie_applepay', 'paypal_paylater', 'sepa'
  const [isLoading, setIsLoading] = useState(isMollieReturnInitial || isPayPalReturnInitial); // Start loading if returning from payment
  const [order, setOrder] = useState(null);
  const [sepaInfo, setSepaInfo] = useState(null);
  const [molliePaymentMethods, setMolliePaymentMethods] = useState([]);
  const [showAGBWarning, setShowAGBWarning] = useState(false);
  const [showPaymentFailedMessage, setShowPaymentFailedMessage] = useState(false);
  const [isRestoringCart, setIsRestoringCart] = useState(false);

  // ============================================================
  // PayPal-Ratenzahlung Eligibility (frontend mirror of backend logic)
  // ============================================================
  const isPayPalInstallment = paymentMethod === 'paypal_paylater';

  // Compute base totals (without surcharge)
  const baseSubtotal = getCartTotal();         // net
  const baseTax = getTaxAmount();              // 19 % MwSt
  const baseGrandTotal = getGrandTotal();      // gross

  // PayPal-Ratenzahlung: 10 % surcharge on GROSS (Brutto-Verkaufspreis).
  // This is a non-VAT pass-through fee — Jannesmann uses it to compensate
  // for PayPal's ~9,74 % merchant fee on the 0 %-Verkäuferfinanzierung.
  // It is added AFTER the standard 19 % VAT calculation, so the MwSt
  // reported to the Finanzamt stays based on the original product net.
  const installmentSurchargeGross = +(baseGrandTotal * 0.10).toFixed(2);
  // For backwards-compat display (some old code paths still reference these)
  const installmentSurchargeNet = installmentSurchargeGross; // gross-only fee, no VAT split
  const installmentSurchargeTax = 0;

  // Displayed totals (used in the right-hand summary box & for order submission)
  const displayedSubtotal = baseSubtotal;        // unchanged
  const displayedTax = baseTax;                  // unchanged (no VAT on the fee)
  const displayedGrandTotal = isPayPalInstallment
    ? baseGrandTotal + installmentSurchargeGross
    : baseGrandTotal;

  // Check whether the cart is eligible for PayPal-Ratenzahlung.
  // We use the FINANCED gross (with +10 %) for the 10k cap test, so the
  // option only appears when the financed price still fits PayPal's limit.
  // Robustly detect accessories: explicit `type` flag, presence of `category:
  // 'Zubehör'`, OR fallback to a backend check. This handles older cart items
  // that were saved before we set `type: 'accessory'`.
  const cartEligibilityResult = useMemo(() => {
    const isAccessoryItem = (it) => {
      if (it.type === 'accessory') return true;
      if (it.category === 'Zubehör') return true;
      if (!it.model && it.slug && it.category == null) return true;
      return false;
    };
    const items = (cart || []).map((it) => ({
      slug: it.slug,
      category: it.category,
      name: it.name,
      isAccessory: isAccessoryItem(it),
      stockQuantity: it.stockQuantity,
      availability: it.availability,
    }));
    const financedGross = +((baseGrandTotal) * (1 + PAYPAL_GROSS_SURCHARGE)).toFixed(2);
    return paypalCartEligible(items, financedGross);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, baseGrandTotal]);

  const payPalInstallmentAvailable = cartEligibilityResult.eligible;

  // If user selects paypal_paylater but cart becomes ineligible (e.g. they
  // add a non-eligible item later), silently fall back to credit card.
  useEffect(() => {
    if (paymentMethod === 'paypal_paylater' && !payPalInstallmentAvailable) {
      setPaymentMethod('mollie_creditcard');
    }
  }, [payPalInstallmentAvailable, paymentMethod]);
  
  // Helper to save cart data AND order info for payment retry
  // PRIVACY: We do NOT save personal form data - only cart items and order reference
  const saveCartForRecovery = (cartItems = null, orderData = null) => {
    const cartToSave = cartItems || cart || JSON.parse(localStorage.getItem('jannesmann_cart') || '[]');
    
    const cartBackup = {
      savedCart: cartToSave,
      savedAt: new Date().toISOString(),
      // Store order reference for retry (so we don't create duplicate orders)
      pendingOrderId: orderData?.id || null,
      pendingOrderNumber: orderData?.order_number || null
    };
    
    // Save cart backup for recovery after failed payment
    sessionStorage.setItem('jannesmann_cart_backup', JSON.stringify(cartBackup));
    localStorage.setItem('jannesmann_cart_backup', JSON.stringify(cartBackup));
    
    console.log('[Checkout] Saved cart backup:', cartToSave.length, 'items', orderData ? `with order ${orderData.order_number}` : '');
  };
  
  // Helper to load cart backup from storage
  const loadCartBackup = () => {
    let saved = sessionStorage.getItem('jannesmann_cart_backup');
    if (saved) {
      return JSON.parse(saved);
    }
    saved = localStorage.getItem('jannesmann_cart_backup');
    if (saved) {
      return JSON.parse(saved);
    }
    return null;
  };
  
  // Helper to clear cart backup
  const clearCartBackup = () => {
    sessionStorage.removeItem('jannesmann_cart_backup');
    localStorage.removeItem('jannesmann_cart_backup');
    // Also clear old checkout data keys for privacy
    sessionStorage.removeItem('jannesmann_checkout_data');
    localStorage.removeItem('jannesmann_checkout_backup');
  };
  
  // Helper to restore cart from backup
  const restoreCartFromBackup = () => {
    const backup = loadCartBackup();
    console.log('[Checkout] Attempting to restore cart from backup:', backup);
    
    if (backup?.savedCart && Array.isArray(backup.savedCart) && backup.savedCart.length > 0) {
      console.log('[Checkout] Restoring cart with', backup.savedCart.length, 'items');
      
      localStorage.setItem('jannesmann_cart', JSON.stringify(backup.savedCart));
      
      if (typeof setCart === 'function') {
        setCart(backup.savedCart);
      }
      return true;
    }
    return false;
  };
  
  // Check if we have cart backup data
  const hasCartBackup = !!loadCartBackup()?.savedCart?.length;
  
  // Check if we have a pending order from a failed payment (for retry)
  const getPendingOrderFromBackup = () => {
    const backup = loadCartBackup();
    if (backup?.pendingOrderId) {
      return {
        id: backup.pendingOrderId,
        order_number: backup.pendingOrderNumber
      };
    }
    return null;
  };
  
  // Form data - NEVER load from storage to protect privacy
  // Checkboxes must ALWAYS start unchecked - user must actively confirm
  const getInitialFormData = () => {
    // Only pre-fill if user is logged in (their own data)
    return {
      customer_name: user?.name || '',
      customer_email: user?.email || '',
      customer_phone: '',
      company: '',
      vat_id: '',
      street: '',
      postal_code: '',
      city: '',
      country: 'Deutschland',
      customer_note: '',
      // CRITICAL: Checkboxes must ALWAYS be false - legal requirement!
      terms_accepted: false,
      privacy_accepted: false,
      cancellation_accepted: false
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData);
  
  // Pre-fill form if user is logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email
      }));
    }
  }, [user]);
  
  // Handle payment retry - restore ONLY cart, NOT personal data
  useEffect(() => {
    if (isPaymentRetry || paymentFailed) {
      // Restore cart from backup
      const cartRestored = restoreCartFromBackup();
      if (cartRestored) {
        // Go directly to payment step
        setStep(2);
        // Show payment failed message
        setShowPaymentFailedMessage(true);
      }
      // Clear the URL params
      setSearchParams({});
    }
  }, [isPaymentRetry, paymentFailed, setSearchParams]);
  
  // REMOVED: Auto-save of form data - privacy concern!
  // We only save cart backup before payment redirect
  
  // Fetch available Mollie payment methods
  useEffect(() => {
    const fetchMollieMethods = async () => {
      try {
        const response = await fetch(`${API_URL}/api/mollie/methods`);
        if (response.ok) {
          const data = await response.json();
          setMolliePaymentMethods(data.methods || []);
        }
      } catch (error) {
        console.error('Error fetching Mollie methods:', error);
      }
    };
    fetchMollieMethods();
  }, []);
  
  // Check if coming back from PayPal
  useEffect(() => {
    const paypalSuccess = searchParams.get('paypal_success');
    const orderId = searchParams.get('order_id');
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    
    if (paypalSuccess === 'true' && orderId && paymentId && payerId) {
      handlePayPalReturn(orderId, paymentId, payerId);
    }
  }, [searchParams]);
  
  const handlePayPalReturn = async (orderId, paymentId, payerId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/orders/paypal/execute/${orderId}?payment_id=${paymentId}&payer_id=${payerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Get order details
        const orderResponse = await fetch(`${API_URL}/api/orders/${orderId}`);
        const orderData = await orderResponse.json();
        setOrder(orderData);
        setStep(3);
        clearCart();
        toast.success('Zahlung erfolgreich!');
      } else {
        toast.error('Zahlung fehlgeschlagen: ' + (result.error || 'Unbekannter Fehler'));
        navigate('/checkout');
      }
    } catch (error) {
      console.error('PayPal return error:', error);
      toast.error('Fehler bei der Zahlungsverarbeitung');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const validateStep1 = () => {
    const required = ['customer_name', 'customer_email', 'street', 'postal_code', 'city'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error('Bitte füllen Sie alle Pflichtfelder aus');
        return false;
      }
    }
    if (!formData.customer_email.includes('@')) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return false;
    }
    return true;
  };
  
  const validateStep2 = () => {
    if (!formData.terms_accepted || !formData.privacy_accepted || !formData.cancellation_accepted) {
      // Show prominent modal instead of small toast
      setShowAGBWarning(true);
      return false;
    }
    return true;
  };
  
  const createOrder = async () => {
    // Calculate preorder values if applicable
    const isPreorder = isPreorderCart;
    const originalTotal = getCartOriginalValue();
    const discountAmount = getTotalDiscount();
    const discountedTotal = getCartFullValue();
    const depositAmount = isPreorder ? getCartTotal() : null; // getCartTotal returns deposit for preorders
    const remainingAmount = isPreorder ? getTotalRemainingAmount() : null;

    // PayPal-Ratenzahlung: the +10 % gross surcharge is sent as a separate
    // field (`paypal_installment_surcharge`) so the backend can treat it as
    // a non-VAT pass-through fee added AFTER tax calculation.
    const itemsForOrder = cart.map(item => ({
      product_id: item.originalId || item.id,
      product_name: item.name,
      product_model: item.model || null,
      quantity: item.quantity,
      unit_price: item.isPreorder ? item.preorderPrice : (item.pricing?.numericPrice || item.price || 0),
      original_price: item.isPreorder ? item.originalPrice : null,
      total_price: item.isPreorder
        ? item.preorderPrice * item.quantity
        : (item.pricing?.numericPrice || item.price || 0) * item.quantity,
      is_preorder_item: item.isPreorder || false
    }));

    const orderData = {
      customer_email: formData.customer_email,
      customer_name: formData.customer_name,
      customer_phone: formData.customer_phone || null,
      shipping_address: {
        name: formData.customer_name,
        street: formData.street,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country,
        phone: formData.customer_phone || null,
        company: formData.company || null,
        vat_id: formData.vat_id || null
      },
      items: itemsForOrder,
      payment_method: paymentMethod,
      customer_note: formData.customer_note || null,
      terms_accepted: formData.terms_accepted,
      privacy_accepted: formData.privacy_accepted,
      cancellation_accepted: formData.cancellation_accepted,
      // Preorder specific fields
      is_preorder: isPreorder,
      preorder_discount_percent: isPreorder ? PREORDER_DISCOUNT * 100 : null, // 10
      preorder_deposit_percent: isPreorder ? PREORDER_DEPOSIT * 100 : null, // 20
      original_total: isPreorder ? originalTotal : null,
      discount_amount: isPreorder ? discountAmount : null,
      discounted_total: isPreorder ? discountedTotal : null,
      deposit_amount: depositAmount,
      remaining_amount: remainingAmount,
      // PayPal-Ratenzahlung: gross surcharge (10 % of brutto total) as a
      // non-VAT pass-through fee — backend adds it to total AFTER tax.
      paypal_installment_surcharge: isPayPalInstallment ? installmentSurchargeGross : null,
    };
    
    const response = await fetch(`${API_URL}/api/orders/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData)
    });
    
    if (!response.ok) {
      throw new Error('Fehler beim Erstellen der Bestellung');
    }
    
    return await response.json();
  };
  
  const handleSEPACheckout = async () => {
    if (!validateStep2()) return;
    
    setIsLoading(true);
    try {
      // Create order
      const orderResult = await createOrder();
      setOrder(orderResult);
      
      // Get SEPA info
      const sepaResponse = await fetch(`${API_URL}/api/orders/sepa-info/${orderResult.id}`);
      const sepaData = await sepaResponse.json();
      setSepaInfo(sepaData);
      
      // Move to confirmation
      setStep(3);
      clearCart();
      clearCartBackup(); // Clear cart backup after successful order
      toast.success('Bestellung erfolgreich erstellt!');
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Fehler beim Checkout: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handlePayPalCheckout = async () => {
    if (!validateStep2()) return;
    
    setIsLoading(true);
    try {
      // IMPORTANT: Save cart backup BEFORE redirect (NOT personal data!)
      saveCartForRecovery(cart);
      
      // Create order first
      const orderResult = await createOrder();
      setOrder(orderResult);
      
      // Create PayPal payment
      const paypalResponse = await fetch(`${API_URL}/api/orders/paypal/create/${orderResult.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const paypalData = await paypalResponse.json();
      
      if (paypalData.success && paypalData.approval_url) {
        // Save cart backup before redirect (NOT personal data!)
        const cartBackup = {
          savedCart: cart,
          savedAt: new Date().toISOString()
        };
        sessionStorage.setItem('jannesmann_cart_backup', JSON.stringify(cartBackup));
        localStorage.setItem('jannesmann_cart_backup', JSON.stringify(cartBackup));
        console.log('[Checkout] Cart backup saved before PayPal redirect, cart items:', cart.length);
        
        // Redirect to PayPal
        window.location.href = paypalData.approval_url;
      } else {
        toast.error('PayPal Fehler: ' + (paypalData.error || 'Unbekannter Fehler'));
      }
      
    } catch (error) {
      console.error('PayPal checkout error:', error);
      toast.error('Fehler beim PayPal Checkout');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle Mollie payments (credit card, klarna, bank transfer)
  const handleMollieCheckout = async (mollieMethod) => {
    if (!validateStep2()) return;
    
    setIsLoading(true);
    try {
      let orderToUse = order; // Use existing order if set (from previous failed attempt)
      
      // Check if we have a pending order from a failed payment retry
      const pendingOrder = getPendingOrderFromBackup();
      
      // Only create a new order if:
      // 1. No order is currently set AND
      // 2. No pending order exists from a previous failed attempt
      if (!orderToUse && !pendingOrder) {
        console.log('[Checkout] Creating new order...');
        const orderResult = await createOrder();
        orderToUse = orderResult;
        setOrder(orderResult);
      } else if (pendingOrder && !orderToUse) {
        // Reuse the pending order from the failed attempt
        console.log('[Checkout] Reusing pending order from failed attempt:', pendingOrder.order_number);
        // Fetch the full order details
        try {
          const orderResponse = await fetch(`${API_URL}/api/orders/${pendingOrder.id}`);
          if (orderResponse.ok) {
            orderToUse = await orderResponse.json();
            setOrder(orderToUse);
          } else {
            // If fetching fails, create a new order
            console.log('[Checkout] Could not fetch pending order, creating new one');
            const orderResult = await createOrder();
            orderToUse = orderResult;
            setOrder(orderResult);
          }
        } catch (e) {
          console.log('[Checkout] Error fetching pending order, creating new one:', e);
          const orderResult = await createOrder();
          orderToUse = orderResult;
          setOrder(orderResult);
        }
      } else {
        console.log('[Checkout] Using existing order:', orderToUse?.order_number);
      }
      
      // Save cart backup with order reference BEFORE creating payment
      saveCartForRecovery(cart, orderToUse);
      
      // Determine the payment amount
      // For preorders: only the deposit amount (20% of discounted net price + 19% tax)
      // For regular orders: full total (already includes tax from order creation)
      const isPreorder = orderToUse.is_preorder || isPreorderCart;
      let paymentAmount;
      
      if (isPreorder) {
        // For preorders, calculate deposit + MwSt
        // deposit_amount from order is the net deposit (20% of discounted net price)
        const netDeposit = orderToUse.deposit_amount || getCartTotal();
        const depositTax = netDeposit * 0.19;
        paymentAmount = netDeposit + depositTax;
        
        console.log('[Checkout] Preorder payment calculation:', {
          netDeposit,
          depositTax,
          grossDeposit: paymentAmount
        });
      } else {
        // Regular order: use the stored total (already gross)
        paymentAmount = orderToUse.total;
      }
      
      // Create description based on order type
      const paymentDescription = isPreorder 
        ? `Anzahlung Vorbestellung ${orderToUse.order_number}` 
        : `Bestellung ${orderToUse.order_number}`;
      
      console.log('[Checkout] Creating Mollie payment:', {
        isPreorder,
        paymentAmount,
        orderTotal: orderToUse.total,
        depositAmount: orderToUse.deposit_amount
      });
      
      // Create new Mollie payment for this order
      const mollieResponse = await fetch(`${API_URL}/api/mollie/create-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: orderToUse.id,
          amount: paymentAmount, // IMPORTANT: Use deposit amount for preorders!
          currency: 'EUR',
          description: paymentDescription,
          customer_email: formData.customer_email || orderToUse.customer_email,
          customer_name: formData.customer_name || orderToUse.customer_name,
          redirect_url: `${window.location.origin}/checkout?mollie_success=true&order_id=${orderToUse.id}`,
          webhook_url: `${API_URL}/api/mollie/webhook`,
          method: mollieMethod, // creditcard, klarna, banktransfer, etc.
          metadata: {
            order_number: orderToUse.order_number,
            customer_email: formData.customer_email || orderToUse.customer_email,
            is_retry: !!pendingOrder,
            is_preorder: isPreorder,
            deposit_payment: isPreorder
          },
          // Include billing address for Klarna and other methods that require it
          billing_address: {
            name: formData.customer_name || orderToUse.customer_name,
            street: formData.street || orderToUse.shipping_address?.street,
            city: formData.city || orderToUse.shipping_address?.city,
            postal_code: formData.postal_code || orderToUse.shipping_address?.postal_code,
            country: formData.country || orderToUse.shipping_address?.country || 'Deutschland'
          },
          // Include order items for Klarna (required)
          items: cart.length > 0 ? cart.map(item => ({
            product_id: item.originalId || item.id,
            product_name: item.name + (item.isPreorder ? ' (Anzahlung)' : ''),
            quantity: item.quantity,
            unit_price: item.isPreorder ? item.depositAmount : (item.pricing?.numericPrice || item.price || 0)
          })) : orderToUse.items?.map(item => ({
            product_id: item.product_id,
            product_name: item.product_name + (orderToUse.is_preorder ? ' (Anzahlung)' : ''),
            quantity: item.quantity,
            unit_price: orderToUse.is_preorder ? (item.unit_price * 0.20) : item.unit_price
          }))
        })
      });
      
      const mollieData = await mollieResponse.json();
      
      if (mollieResponse.ok && mollieData.checkout_url) {
        // Save cart backup with order reference before redirect
        saveCartForRecovery(cart, orderToUse);
        console.log('[Checkout] Cart backup saved before Mollie redirect, cart items:', cart.length, 'order:', orderToUse.order_number);
        
        // Redirect to Mollie checkout page (for ALL payment methods including banktransfer)
        window.location.href = mollieData.checkout_url;
      } else {
        // Show error with helpful message
        const errorMsg = mollieData.detail || 'Unbekannter Fehler';
        
        // Check if it's a high amount error
        if (errorMsg.includes('zu hoch') || errorMsg.includes('maximum')) {
          toast.error('Der Betrag ist für Online-Zahlung zu hoch. Bitte wählen Sie "Manuelle Überweisung" oder kontaktieren Sie uns.', {
            duration: 8000
          });
        } else {
          toast.error('Mollie Fehler: ' + errorMsg);
        }
      }
      
    } catch (error) {
      console.error('Mollie checkout error:', error);
      toast.error('Fehler beim Checkout: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Check for Mollie payment return - must run before cart check
  useEffect(() => {
    const mollieSuccess = searchParams.get('mollie_success');
    const orderId = searchParams.get('order_id');
    
    if (mollieSuccess === 'true' && orderId) {
      handleMollieReturn(orderId);
    }
  }, [searchParams]);
  
  const handleMollieReturn = async (orderId) => {
    setIsLoading(true);
    try {
      // Get payment status from Mollie (fresh status directly from Mollie API)
      const paymentResponse = await fetch(`${API_URL}/api/mollie/payment-by-order/${orderId}`);
      
      if (!paymentResponse.ok) {
        throw new Error('Zahlung nicht gefunden');
      }
      
      const paymentData = await paymentResponse.json();
      console.log('[Checkout] Mollie payment status:', paymentData);
      
      // Get order details
      const orderResponse = await fetch(`${API_URL}/api/orders/${orderId}`);
      if (!orderResponse.ok) {
        throw new Error('Bestellung nicht gefunden');
      }
      const orderData = await orderResponse.json();
      
      setOrder(orderData);
      
      // Determine payment status - check multiple indicators
      const status = paymentData.current_status?.toLowerCase();
      const isPaid = paymentData.is_paid === true;
      
      console.log('[Checkout] Payment status check - status:', status, 'isPaid:', isPaid);
      
      // SUCCESS: paid or authorized
      if (isPaid || status === 'paid' || status === 'authorized') {
        console.log('[Checkout] Payment SUCCESS - clearing cart');
        clearCart();
        clearCartBackup();
        setStep(3);
        // No toast - success page shows confirmation
      } 
      // PENDING: open or pending (e.g. SEPA bank transfer)
      else if (status === 'pending' || status === 'open') {
        console.log('[Checkout] Payment PENDING - waiting for payment');
        // Do NOT clear cart yet - payment is not complete!
        setStep(3);
        setOrder({
          ...orderData,
          _payment_pending: true // Mark as pending
        });
        // No toast - pending message shown on confirmation page
      } 
      // EXPIRED: Payment deadline passed - show special expired page
      else if (status === 'expired') {
        console.log('[Checkout] Payment EXPIRED - showing expired page');
        
        // Clear cart backup since order is canceled
        clearCartBackup();
        
        // Show expired page with order info
        setOrder({
          ...orderData,
          _payment_expired: true
        });
        setStep(3);
        setSearchParams({});
      }
      // FAILED/CANCELED: Payment failed or canceled - allow retry
      else if (status === 'canceled' || status === 'failed') {
        console.log('[Checkout] Payment FAILED/CANCELED - restoring cart for retry');
        
        // Restore the cart from backup
        const cartRestored = restoreCartFromBackup();
        console.log('[Checkout] Cart restored:', cartRestored);
        
        // Keep the order reference for retry
        const cartBackup = {
          savedCart: cart.length > 0 ? cart : JSON.parse(localStorage.getItem('jannesmann_cart') || '[]'),
          savedAt: new Date().toISOString(),
          pendingOrderId: orderData.id,
          pendingOrderNumber: orderData.order_number
        };
        sessionStorage.setItem('jannesmann_cart_backup', JSON.stringify(cartBackup));
        localStorage.setItem('jannesmann_cart_backup', JSON.stringify(cartBackup));
        
        // Keep the order in state for retry
        setOrder(orderData);
        
        // Pre-fill form data from the order for retry
        setFormData(prev => ({
          ...prev,
          customer_name: orderData.customer_name || prev.customer_name,
          customer_email: orderData.customer_email || prev.customer_email,
          customer_phone: orderData.customer_phone || prev.customer_phone,
          company: orderData.shipping_address?.company || prev.company,
          vat_id: orderData.shipping_address?.vat_id || prev.vat_id,
          street: orderData.shipping_address?.street || prev.street,
          postal_code: orderData.shipping_address?.postal_code || prev.postal_code,
          city: orderData.shipping_address?.city || prev.city,
          country: orderData.shipping_address?.country || prev.country || 'Deutschland',
          customer_note: orderData.customer_note || prev.customer_note,
          terms_accepted: orderData.terms_accepted || false,
          privacy_accepted: orderData.privacy_accepted || false,
          cancellation_accepted: orderData.cancellation_accepted || false
        }));
        
        setStep(2); // Go back to payment selection
        setShowPaymentFailedMessage(true); // Show inline alert only - NO TOAST
        setSearchParams({});
        // NO toast.error() - inline alert is sufficient
      } 
      // UNKNOWN: treat as pending
      else {
        console.log('[Checkout] Payment status UNKNOWN:', status, '- treating as pending');
        setStep(3);
        setOrder({
          ...orderData,
          _payment_pending: true
        });
      }
    } catch (error) {
      console.error('Mollie return error:', error);
      toast.error('Fehler bei der Zahlungsverarbeitung: ' + error.message);
      // On error, restore cart
      restoreCartFromBackup();
      setStep(2);
      setShowPaymentFailedMessage(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };
  
  // Check if we're returning from Mollie payment
  const isMollieReturn = searchParams.get('mollie_success') === 'true' && searchParams.get('order_id');
  
  // CRITICAL: Restore cart IMMEDIATELY on component mount if we have backup data
  // This runs before any render checks
  // Also clean up any old personal data that shouldn't be stored
  useEffect(() => {
    // PRIVACY: Remove old checkout data that contained personal info
    sessionStorage.removeItem('jannesmann_checkout_data');
    localStorage.removeItem('jannesmann_checkout_backup');
    
    // Only restore cart backup if exists
    const backup = loadCartBackup();
    if (backup?.savedCart && backup.savedCart.length > 0) {
      const currentCart = JSON.parse(localStorage.getItem('jannesmann_cart') || '[]');
      if (currentCart.length === 0) {
        console.log('[Checkout] Restoring cart from backup on mount:', backup.savedCart.length, 'items');
        localStorage.setItem('jannesmann_cart', JSON.stringify(backup.savedCart));
        if (typeof setCart === 'function') {
          setCart(backup.savedCart);
        }
      }
    }
  }, []); // Run once on mount
  
  // Try to restore cart if empty but we have backup
  useEffect(() => {
    if (cart.length === 0 && hasCartBackup && !isRestoringCart) {
      console.log('[Checkout] Cart is empty but we have backup - restoring');
      setIsRestoringCart(true);
      const restored = restoreCartFromBackup();
      if (restored) {
        toast.info('Ihr Warenkorb wurde wiederhergestellt.');
      }
      setIsRestoringCart(false);
    }
  }, [cart.length, hasCartBackup, isRestoringCart]);
  
  // Show loading state when returning from Mollie
  if (isMollieReturn && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <Card className="text-center p-12">
            <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Zahlung wird verarbeitet...</h2>
            <p className="text-gray-600">Bitte warten Sie einen Moment.</p>
          </Card>
        </div>
      </div>
    );
  }
  
  // Empty cart redirect - but not when returning from Mollie or have backup
  if (cart.length === 0 && step !== 3 && !isMollieReturn && !isLoading && !hasCartBackup && !isRestoringCart) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <Card className="text-center p-12">
            <h2 className="text-2xl font-bold mb-4">Ihr Warenkorb ist leer</h2>
            <p className="text-gray-600 mb-8">Bitte fügen Sie Produkte hinzu, bevor Sie zur Kasse gehen.</p>
            <Button onClick={() => navigate('/maschinen')} className="bg-red-600 hover:bg-red-700">
              <ArrowLeft className="mr-2" size={18} />
              Zu den Maschinen
            </Button>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[
              { num: 1, label: 'Adresse' },
              { num: 2, label: 'Zahlung' },
              { num: 3, label: 'Bestätigung' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center ${step >= s.num ? 'text-red-600' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s.num ? 'bg-red-600 text-white' : 'bg-gray-200'
                  }`}>
                    {step > s.num ? <CheckCircle size={20} /> : s.num}
                  </div>
                  <span className="ml-2 font-medium hidden sm:inline">{s.label}</span>
                </div>
                {i < 2 && (
                  <div className={`w-12 sm:w-24 h-1 ${step > s.num ? 'bg-red-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Address */}
            {step === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Truck className="mr-2 text-red-600" />
                    Lieferadresse
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Personal Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer_name">Name *</Label>
                      <Input
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleInputChange}
                        placeholder="Max Mustermann"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer_email">E-Mail *</Label>
                      <Input
                        id="customer_email"
                        name="customer_email"
                        type="email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        placeholder="max@example.de"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer_phone">Telefon</Label>
                      <Input
                        id="customer_phone"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        placeholder="+49 123 456789"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Firma (optional)</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Musterfirma GmbH"
                      />
                    </div>
                  </div>
                  
                  {formData.company && (
                    <div>
                      <Label htmlFor="vat_id">USt-IdNr. (optional)</Label>
                      <Input
                        id="vat_id"
                        name="vat_id"
                        value={formData.vat_id}
                        onChange={handleInputChange}
                        placeholder="DE123456789"
                      />
                    </div>
                  )}
                  
                  {/* Address */}
                  <div>
                    <Label htmlFor="street">Straße und Hausnummer *</Label>
                    <Input
                      id="street"
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                      placeholder="Musterstraße 123"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="postal_code">PLZ *</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        placeholder="12345"
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="city">Stadt *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Musterstadt"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="customer_note">Anmerkungen zur Bestellung</Label>
                    <textarea
                      id="customer_note"
                      name="customer_note"
                      value={formData.customer_note}
                      onChange={handleInputChange}
                      className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      rows={3}
                      placeholder="Besondere Hinweise zur Lieferung..."
                    />
                  </div>
                  
                  <Button 
                    onClick={() => validateStep1() && setStep(2)}
                    className={`w-full py-6 text-lg ${isPreorderCart ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-600 hover:bg-red-700'}`}
                  >
                    {isPreorderCart ? (
                      <>
                        <Package className="mr-2" />
                        Vorbestellung mit 20% Anzahlung fortsetzen
                      </>
                    ) : (
                      <>
                        Weiter zur Zahlung
                        <ArrowRight className="ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* Step 2: Payment */}
            {step === 2 && (
              <Card>
                <CardHeader className={isPreorderCart ? 'bg-amber-50' : ''}>
                  <CardTitle className="flex items-center">
                    <CreditCard className={`mr-2 ${isPreorderCart ? 'text-amber-600' : 'text-red-600'}`} />
                    {isPreorderCart ? 'Anzahlung bezahlen' : 'Zahlungsmethode wählen'}
                  </CardTitle>
                  {isPreorderCart && (
                    <p className="text-sm text-amber-700 mt-1">
                      Sie zahlen jetzt nur die 20% Anzahlung – Restzahlung bei Lieferung
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Payment Failed Warning Banner - PROMINENT */}
                  {showPaymentFailedMessage && (
                    <div className="bg-red-50 border-2 border-red-500 p-6 rounded-xl mb-6 shadow-lg">
                      <div className="flex items-start">
                        <div className="bg-red-100 rounded-full p-3 mr-4 flex-shrink-0">
                          <AlertTriangle className="text-red-600" size={32} />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-red-800 font-bold text-xl mb-2">Zahlung nicht erfolgreich</h4>
                          <p className="text-red-700 text-base leading-relaxed">
                            Die vorherige Zahlung wurde abgebrochen oder ist fehlgeschlagen. 
                            Ihr Warenkorb wurde beibehalten.
                            <strong className="block mt-2">Bitte wählen Sie eine andere Zahlungsart oder versuchen Sie es erneut.</strong>
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-4 text-red-700 border-red-300 hover:bg-red-100"
                            onClick={() => setShowPaymentFailedMessage(false)}
                          >
                            Meldung schließen
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Payment Method Selection */}
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 font-medium">Online-Zahlung (Sofortzahlung)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Credit Card via Mollie */}
                      <div
                        onClick={() => setPaymentMethod('mollie_creditcard')}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          paymentMethod === 'mollie_creditcard' 
                            ? 'border-red-600 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center mb-2">
                          <CreditCard className={`mr-2 ${paymentMethod === 'mollie_creditcard' ? 'text-red-600' : 'text-gray-500'}`} />
                          <span className="font-bold">Kreditkarte</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          {/* Visa - Inline SVG */}
                          <div className="bg-white rounded border border-gray-200 px-2 py-1 h-8 flex items-center justify-center">
                            <svg viewBox="0 0 750 471" className="h-5 w-auto">
                              <path d="M278.198 334.228l33.36-195.763h53.358l-33.384 195.763h-53.334zm246.11-191.54c-10.57-3.966-27.135-8.222-47.822-8.222-52.725 0-89.863 26.551-90.18 64.604-.297 28.129 26.515 43.822 46.754 53.185 20.77 9.597 27.752 15.716 27.654 24.283-.133 13.123-16.586 19.116-31.924 19.116-21.355 0-32.701-2.967-50.225-10.274l-6.878-3.111-7.487 43.822c12.463 5.466 35.508 10.199 59.438 10.445 56.09 0 92.502-26.248 92.916-66.884.199-22.27-14.016-39.216-44.801-53.188-18.65-9.056-30.072-15.099-29.951-24.269 0-8.137 9.668-16.838 30.56-16.838 17.446-.271 30.089 3.527 39.936 7.496l4.781 2.26 7.229-42.425m137.307-4.223h-41.232c-12.773 0-22.332 3.486-27.94 16.234l-79.245 179.404h56.031s9.159-24.121 11.231-29.418c6.123 0 60.555.084 68.336.084 1.596 6.854 6.492 29.334 6.492 29.334h49.512l-43.185-195.638zm-65.417 126.408c4.414-11.279 21.26-54.724 21.26-54.724-.314.521 4.381-11.334 7.074-18.684l3.607 16.878s10.217 46.729 12.352 56.527h-44.293v.003zm-363.667-126.408l-52.24 133.496-5.567-27.129c-9.726-31.274-40.025-65.157-73.898-82.12l47.767 171.203 56.455-.063 84.004-195.386-56.521-.001" fill="#1A1F71"/>
                              <path d="M146.92 138.465H60.879l-.682 4.073c66.939 16.204 111.232 55.363 129.618 102.415l-18.709-89.96c-3.229-12.396-12.597-16.095-24.186-16.528" fill="#F9A533"/>
                            </svg>
                          </div>
                          {/* Mastercard */}
                          <div className="bg-white rounded border border-gray-200 px-2 py-1 h-8 flex items-center justify-center">
                            <img src="https://customer-assets.emergentagent.com/job_c8ae81ef-39b2-4170-b30f-e165111bc7d2/artifacts/gwxfm1bl_MasterCard_Logo.svg.png" alt="Mastercard" className="h-5 w-auto object-contain" />
                          </div>
                          {/* American Express - Inline SVG */}
                          <div className="bg-white rounded border border-gray-200 px-2 py-1 h-8 flex items-center justify-center">
                            <svg viewBox="0 0 60 40" className="h-5 w-auto">
                              <rect width="60" height="40" rx="4" fill="#016FD0"/>
                              <text x="30" y="26" textAnchor="middle" fill="white" fontSize="12" fontFamily="Arial, sans-serif" fontWeight="bold">AMEX</text>
                            </svg>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">Sichere Zahlung, sofortiger Versand</p>
                      </div>
                      
                      {/* Apple Pay via Mollie */}
                      <div
                        onClick={() => setPaymentMethod('mollie_applepay')}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          paymentMethod === 'mollie_applepay' 
                            ? 'border-red-600 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center mb-2">
                          <img src="https://www.mollie.com/external/icons/payment-methods/applepay.svg" alt="Apple Pay" className="h-6 mr-2" />
                          <span className="font-bold">Apple Pay</span>
                        </div>
                        <p className="text-sm text-gray-600">Schnelle Zahlung mit Apple</p>
                        <p className="text-xs text-gray-500 mt-1">Touch ID / Face ID</p>
                      </div>
                      
                      {/* Klarna via Mollie */}
                      <div
                        onClick={() => setPaymentMethod('mollie_klarna')}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          paymentMethod === 'mollie_klarna' 
                            ? 'border-red-600 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center mb-2">
                          <img src="https://www.mollie.com/external/icons/payment-methods/klarna.svg" alt="Klarna" className="h-6 mr-2" />
                          <span className="font-bold">Klarna</span>
                        </div>
                        <p className="text-sm text-gray-600">Rechnung oder Ratenzahlung</p>
                        <p className="text-xs text-gray-500 mt-1">Später bezahlen möglich</p>
                      </div>
                      
                      {/* SEPA Bank Transfer via Mollie */}
                      <div
                        onClick={() => setPaymentMethod('mollie_banktransfer')}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          paymentMethod === 'mollie_banktransfer' 
                            ? 'border-red-600 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center mb-2">
                          {/* SEPA Icon - gleich wie im Footer */}
                          <div className="bg-white rounded border border-gray-200 px-2 py-1 h-8 flex items-center justify-center mr-2">
                            <img src="https://www.mollie.com/external/icons/payment-methods/banktransfer.svg" alt="SEPA" className="h-5 w-auto" />
                          </div>
                          <span className="font-bold">SEPA-Überweisung</span>
                        </div>
                        <p className="text-sm text-gray-600">Banküberweisung (Vorkasse)</p>
                        <p className="text-xs text-gray-500 mt-1">Versand nach Zahlungseingang</p>
                      </div>

                      {/* PayPal-Ratenzahlung — nur anzeigen, wenn Cart eligible */}
                      {payPalInstallmentAvailable && (
                        <div
                          onClick={() => setPaymentMethod('paypal_paylater')}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            paymentMethod === 'paypal_paylater'
                              ? 'border-blue-700 bg-blue-50'
                              : 'border-blue-200 hover:border-blue-400'
                          }`}
                          data-testid="payment-method-paypal-paylater"
                        >
                          <div className="flex items-center mb-2 flex-wrap gap-2">
                            <img src="https://www.mollie.com/external/icons/payment-methods/paypal.svg" alt="PayPal" className="h-6 mr-1" />
                            <span className="font-bold">PayPal-Ratenzahlung</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-green-600 text-white tracking-wide">0 % FINANZIERUNG</span>
                          </div>
                          <p className="text-sm text-gray-700">Bequem in monatlichen Raten – <strong>0 % Zinsen für Sie</strong></p>
                          <p className="text-xs text-gray-500 mt-1">
                            Bis 10.000 € · Nur für <strong>Privatkunden</strong> mit Privat-PayPal-Konto · Finanzierung übernimmt Jannesmann Baumaschinen
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Hinweis wenn Cart NICHT eligible aber Brutto-Total nahe 10k */}
                    {!payPalInstallmentAvailable && cartEligibilityResult.reason && cart.length > 0 && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                        <strong>PayPal-Ratenzahlung nicht verfügbar:</strong> {cartEligibilityResult.reason}
                      </div>
                    )}
                    
                    {/* Alternative: Manual SEPA */}
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm text-gray-600 font-medium mb-3">Alternative Zahlungsart</p>
                      <div
                        onClick={() => setPaymentMethod('sepa')}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          paymentMethod === 'sepa' 
                            ? 'border-red-600 bg-red-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center mb-2">
                          <Wallet className={`mr-2 ${paymentMethod === 'sepa' ? 'text-red-600' : 'text-gray-500'}`} />
                          <span className="font-bold">Manuelle Überweisung</span>
                        </div>
                        <p className="text-sm text-gray-600">Sie erhalten unsere Bankdaten per E-Mail</p>
                        <p className="text-xs text-gray-500 mt-1">Versand nach Zahlungseingang</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Legal Checkboxes */}
                  <div className="space-y-3 border-t pt-6">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        name="terms_accepted"
                        checked={formData.terms_accepted}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        Ich akzeptiere die <a href="/agb" className="text-red-600 hover:underline">AGB</a> *
                      </span>
                    </label>
                    
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        name="privacy_accepted"
                        checked={formData.privacy_accepted}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        Ich habe die <a href="/datenschutz" className="text-red-600 hover:underline">Datenschutzerklärung</a> gelesen *
                      </span>
                    </label>
                    
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        name="cancellation_accepted"
                        checked={formData.cancellation_accepted}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        Ich akzeptiere die <a href="/widerruf" className="text-red-600 hover:underline">Widerrufsbelehrung</a> *
                      </span>
                    </label>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setStep(1)}
                      className="flex-1"
                    >
                      <ArrowLeft className="mr-2" />
                      Zurück
                    </Button>
                    
                    {/* Manual SEPA */}
                    {paymentMethod === 'sepa' && (
                      <Button 
                        onClick={handleSEPACheckout}
                        disabled={isLoading}
                        className="flex-1 bg-red-600 hover:bg-red-700 py-6 text-lg"
                      >
                        {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wird verarbeitet...</> : 'Kostenpflichtig bestellen'}
                        {!isLoading && <ArrowRight className="ml-2" />}
                      </Button>
                    )}
                    
                    {/* Mollie Credit Card */}
                    {paymentMethod === 'mollie_creditcard' && (
                      <Button 
                        onClick={() => handleMollieCheckout('creditcard')}
                        disabled={isLoading}
                        className="flex-1 bg-red-600 hover:bg-red-700 py-6 text-lg"
                      >
                        {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wird verarbeitet...</> : 'Mit Kreditkarte bezahlen'}
                        {!isLoading && <CreditCard className="ml-2" />}
                      </Button>
                    )}
                    
                    {/* Mollie Apple Pay */}
                    {paymentMethod === 'mollie_applepay' && (
                      <Button 
                        onClick={() => handleMollieCheckout('applepay')}
                        disabled={isLoading}
                        className="flex-1 bg-black hover:bg-gray-800 py-6 text-lg"
                      >
                        {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wird verarbeitet...</> : 'Mit Apple Pay bezahlen'}
                        {!isLoading && <ArrowRight className="ml-2" />}
                      </Button>
                    )}
                    
                    {/* Mollie Klarna */}
                    {paymentMethod === 'mollie_klarna' && (
                      <Button 
                        onClick={() => handleMollieCheckout('klarna')}
                        disabled={isLoading}
                        className="flex-1 bg-[#FFB3C7] hover:bg-[#FF9AB2] text-black py-6 text-lg"
                      >
                        {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wird verarbeitet...</> : 'Mit Klarna bezahlen'}
                        {!isLoading && <ArrowRight className="ml-2" />}
                      </Button>
                    )}
                    
                    {/* Mollie Bank Transfer */}
                    {paymentMethod === 'mollie_banktransfer' && (
                      <Button 
                        onClick={() => handleMollieCheckout('banktransfer')}
                        disabled={isLoading}
                        className="flex-1 bg-red-600 hover:bg-red-700 py-6 text-lg"
                      >
                        {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wird verarbeitet...</> : 'Per Überweisung bestellen'}
                        {!isLoading && <Building2 className="ml-2" />}
                      </Button>
                    )}

                    {/* PayPal-Ratenzahlung — direkt über PayPal-SDK mit fundingSource=paylater */}
                    {paymentMethod === 'paypal_paylater' && (
                      <Button
                        onClick={() => handlePayPalCheckout()}
                        disabled={isLoading}
                        className="flex-1 bg-blue-700 hover:bg-blue-800 py-6 text-lg"
                        data-testid="checkout-btn-paypal-paylater"
                      >
                        {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Wird verarbeitet...</> : 'Mit PayPal-Ratenzahlung bestellen'}
                        {!isLoading && <CreditCard className="ml-2" />}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Step 3: Confirmation */}
            {step === 3 && order && (
              <Card>
                <CardHeader className={`${order._payment_expired ? 'bg-gray-100' : order._payment_pending ? 'bg-amber-50' : 'bg-green-50'} border-b`}>
                  <CardTitle className={`flex items-center ${order._payment_expired ? 'text-gray-700' : order._payment_pending ? 'text-amber-700' : 'text-green-700'}`}>
                    {order._payment_expired ? (
                      <>
                        <Clock className="mr-2" />
                        Zahlungsfrist abgelaufen – Bestellung storniert
                      </>
                    ) : order._payment_pending ? (
                      <>
                        <Clock className="mr-2" />
                        Bestellung aufgenommen – Überweisung ausstehend
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2" />
                        Bestellung erfolgreich!
                      </>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  {/* EXPIRED State */}
                  {order._payment_expired ? (
                    <div className="mb-6">
                      {/* Header Section */}
                      <div className="text-center mb-6">
                        <div className="bg-gray-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                          <Clock className="text-gray-500" size={40} />
                        </div>
                        <h2 className="text-xl font-bold text-gray-700 mb-2">
                          Zahlungsfrist abgelaufen – Bestellung storniert
                        </h2>
                        <p className="text-lg text-gray-500 mt-2">Bestellnummer: {order.order_number}</p>
                      </div>
                      
                      {/* Info Box */}
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-6">
                        <div className="flex items-start gap-3">
                          <div className="bg-gray-200 rounded-full p-2 flex-shrink-0">
                            <Clock className="text-gray-500" size={20} />
                          </div>
                          <div>
                            <p className="text-gray-700 leading-relaxed">
                              Für diese Bestellung ist innerhalb der Frist keine SEPA-Überweisung eingegangen.
                            </p>
                            <p className="text-gray-600 mt-2 leading-relaxed">
                              Die Zahlungsanforderung ist daher abgelaufen und die Bestellung wurde storniert.
                            </p>
                            <p className="text-gray-800 mt-3 leading-relaxed font-medium">
                              Bitte legen Sie den Artikel erneut in den Warenkorb und führen Sie die Bestellung erneut aus.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                          onClick={() => navigate('/maschinen')}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <ShoppingCart className="mr-2" size={18} />
                          Zurück zum Shop
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => navigate('/')}
                        >
                          Zur Startseite
                        </Button>
                      </div>
                      
                      {/* Contact Note */}
                      <div className="mt-6 text-center">
                        <p className="text-sm text-gray-500">
                          Bei Fragen kontaktieren Sie uns unter{' '}
                          <a href="mailto:info@jannesmann.de" className="text-red-600 hover:underline">info@jannesmann.de</a>
                        </p>
                      </div>
                    </div>
                  ) : order._payment_pending ? (
                    <div className="mb-6">
                      {/* Header Section */}
                      <div className="text-center mb-6">
                        <div className="bg-amber-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                          <Building2 className="text-amber-600" size={40} />
                        </div>
                        <h2 className="text-xl font-bold text-amber-800 mb-2">
                          Zahlung noch nicht eingegangen – Überweisung ausstehend
                        </h2>
                        <p className="text-2xl font-bold text-red-600 mt-2">{order.order_number}</p>
                      </div>
                      
                      {/* Info Box */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                        <div className="flex items-start gap-3">
                          <div className="bg-amber-100 rounded-full p-2 flex-shrink-0">
                            <Clock className="text-amber-600" size={20} />
                          </div>
                          <div>
                            <p className="text-amber-900 leading-relaxed">
                              <strong>Wir haben Ihre Bestellung erhalten.</strong> Die Zahlung per SEPA-Überweisung ist noch nicht eingegangen.
                            </p>
                            <p className="text-amber-800 mt-2 leading-relaxed">
                              Bitte überweisen Sie den Betrag mit dem angegebenen Verwendungszweck. 
                              <strong> Nach Zahlungseingang wird Ihre Bestellung automatisch weiterbearbeitet</strong> und Sie erhalten eine Bestätigung per E-Mail.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Bank Transfer Details for Mollie Banktransfer */}
                      <div className="bg-white border-2 border-amber-300 rounded-xl p-6 shadow-sm">
                        <h3 className="font-bold text-lg mb-4 flex items-center text-gray-800">
                          <Building2 className="mr-2 text-amber-600" />
                          Überweisungsdaten
                        </h3>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                            <div>
                              <p className="text-sm text-gray-500">Empfänger</p>
                              <p className="font-medium">JANNESMANN HANDELS UND IMPORT GbR</p>
                            </div>
                            <button onClick={() => copyToClipboard('JANNESMANN HANDELS UND IMPORT GbR')} className="text-gray-400 hover:text-gray-600 p-2">
                              <Copy size={18} />
                            </button>
                          </div>
                          
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                            <div>
                              <p className="text-sm text-gray-500">IBAN</p>
                              <p className="font-medium font-mono">DE23 4245 1220 0000 0076 33</p>
                            </div>
                            <button onClick={() => copyToClipboard('DE23424512200000007633')} className="text-gray-400 hover:text-gray-600 p-2">
                              <Copy size={18} />
                            </button>
                          </div>
                          
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                            <div>
                              <p className="text-sm text-gray-500">BIC</p>
                              <p className="font-medium font-mono">WELADED1BOT</p>
                            </div>
                            <button onClick={() => copyToClipboard('WELADED1BOT')} className="text-gray-400 hover:text-gray-600 p-2">
                              <Copy size={18} />
                            </button>
                          </div>
                          
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border">
                            <div>
                              <p className="text-sm text-gray-500">Bank</p>
                              <p className="font-medium">Sparkasse Bottrop</p>
                            </div>
                          </div>
                          
                          <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg border-2 border-red-300">
                            <div>
                              <p className="text-sm text-red-600 font-medium">Verwendungszweck (wichtig!)</p>
                              <p className="font-bold text-red-700 text-lg">{order.order_number}</p>
                            </div>
                            <button onClick={() => copyToClipboard(order.order_number)} className="text-red-400 hover:text-red-600 p-2">
                              <Copy size={18} />
                            </button>
                          </div>
                          
                          <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border-2 border-green-300">
                            <div>
                              <p className="text-sm text-green-600 font-medium">Zu überweisender Betrag</p>
                              <p className="font-bold text-green-700 text-2xl">{formatPrice(order.total)}</p>
                            </div>
                            <button onClick={() => copyToClipboard(order.total?.toFixed(2) || '')} className="text-green-400 hover:text-green-600 p-2">
                              <Copy size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Note */}
                      <div className="mt-4 text-center">
                        <p className="text-sm text-gray-500">
                          Bei Fragen zu Ihrer Bestellung kontaktieren Sie uns unter{' '}
                          <a href="mailto:info@jannesmann.de" className="text-red-600 hover:underline">info@jannesmann.de</a>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center mb-6">
                      <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="text-green-600" size={40} />
                      </div>
                      <p className="text-xl font-medium text-green-700">Vielen Dank für Ihre Bestellung!</p>
                      <p className="text-2xl font-bold text-red-600 mt-2">{order.order_number}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Eine Bestätigung wurde an {order.customer_email} gesendet.
                      </p>
                    </div>
                  )}
                  
                  {/* SEPA Payment Info */}
                  {order.payment_method === 'sepa' && sepaInfo && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                      <h3 className="font-bold text-lg mb-4 flex items-center">
                        <Building2 className="mr-2 text-yellow-600" />
                        Bitte überweisen Sie den Betrag:
                      </h3>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-white rounded border">
                          <div>
                            <p className="text-sm text-gray-500">Empfänger</p>
                            <p className="font-medium">{sepaInfo.account_holder}</p>
                          </div>
                          <button onClick={() => copyToClipboard(sepaInfo.account_holder)} className="text-gray-400 hover:text-gray-600">
                            <Copy size={18} />
                          </button>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-white rounded border">
                          <div>
                            <p className="text-sm text-gray-500">IBAN</p>
                            <p className="font-medium font-mono">{sepaInfo.iban}</p>
                          </div>
                          <button onClick={() => copyToClipboard(sepaInfo.iban.replace(/\s/g, ''))} className="text-gray-400 hover:text-gray-600">
                            <Copy size={18} />
                          </button>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-white rounded border">
                          <div>
                            <p className="text-sm text-gray-500">BIC</p>
                            <p className="font-medium font-mono">{sepaInfo.bic}</p>
                          </div>
                          <button onClick={() => copyToClipboard(sepaInfo.bic)} className="text-gray-400 hover:text-gray-600">
                            <Copy size={18} />
                          </button>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-white rounded border">
                          <div>
                            <p className="text-sm text-gray-500">Bank</p>
                            <p className="font-medium">{sepaInfo.bank_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-red-50 rounded border border-red-200">
                          <div>
                            <p className="text-sm text-red-600 font-medium">Verwendungszweck</p>
                            <p className="font-bold text-red-700">{sepaInfo.reference}</p>
                          </div>
                          <button onClick={() => copyToClipboard(sepaInfo.reference)} className="text-red-400 hover:text-red-600">
                            <Copy size={18} />
                          </button>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded border border-green-200">
                          <div>
                            <p className="text-sm text-green-600 font-medium">Betrag</p>
                            <p className="font-bold text-green-700 text-xl">{formatPrice(sepaInfo.amount)}</p>
                          </div>
                          <button onClick={() => copyToClipboard(sepaInfo.amount.toFixed(2))} className="text-green-400 hover:text-green-600">
                            <Copy size={18} />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-yellow-700 mt-4">
                        Nach Zahlungseingang wird Ihre Bestellung bearbeitet und versendet.
                      </p>
                    </div>
                  )}
                  
                  {/* PayPal Confirmation */}
                  {order.payment_method === 'paypal' && order.payment_status === 'paid' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <CheckCircle className="mx-auto text-green-600 mb-4" size={48} />
                      <h3 className="font-bold text-lg text-green-700">Zahlung erfolgreich!</h3>
                      <p className="text-green-600 mt-2">Ihre Zahlung via PayPal wurde bestätigt.</p>
                    </div>
                  )}
                  
                  {/* Download Invoice */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <a 
                      href={`${API_URL}/api/orders/invoice/${order.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button variant="outline" className="w-full">
                        <Download className="mr-2" />
                        Rechnung herunterladen
                      </Button>
                    </a>
                    
                    <Button 
                      onClick={() => navigate('/')}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      Zurück zur Startseite
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader className={`border-b ${isPreorderCart ? 'bg-amber-50' : 'bg-gray-50'}`}>
                <CardTitle className="text-lg flex items-center">
                  {isPreorderCart && <Package className="mr-2 text-amber-600" size={20} />}
                  {isPreorderCart ? 'Vorbestellung' : 'Bestellübersicht'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {/* Preorder Banner */}
                {isPreorderCart && step !== 3 && (
                  <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-800 font-medium flex items-center">
                      <Percent size={16} className="mr-2" />
                      Vorbestellung mit 10% Rabatt
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      Nur 20% Anzahlung – Restzahlung bei Lieferung
                    </p>
                  </div>
                )}
                
                {/* Items */}
                <div className="space-y-3 mb-4">
                  {(step === 3 && order ? order.items : cart).map((item, index) => (
                    <div key={index} className="text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">
                          {item.quantity || 1}x {item.name || item.product_name}
                        </span>
                        {item.isPreorder ? (
                          <span className="font-medium text-amber-700">
                            {formatPrice(item.preorderPrice * (item.quantity || 1))}
                          </span>
                        ) : (
                          <span className="font-medium">
                            {formatPrice((item.pricing?.numericPrice || item.unit_price || 0) * (item.quantity || 1))}
                          </span>
                        )}
                      </div>
                      {item.isPreorder && (
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="line-through">{formatPrice(item.originalPrice * (item.quantity || 1))}</span>
                          <span className="text-green-600 ml-2">–10% Rabatt</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Price Breakdown for Preorders */}
                {isPreorderCart && step !== 3 ? (
                  <>
                    <div className="border-t pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Originalpreis (netto)</span>
                        <span className="line-through text-gray-400">{formatPrice(getCartOriginalValue())}</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600">
                        <span className="flex items-center">
                          <Percent size={14} className="mr-1" />
                          Vorbestellrabatt (10%)
                        </span>
                        <span>–{formatPrice(getTotalDiscount())}</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium">
                        <span>Reduzierter Preis (netto)</span>
                        <span>{formatPrice(getCartFullValue())}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>MwSt. (19%)</span>
                        <span>{formatPrice(getCartFullValue() * 0.19)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium border-t pt-2">
                        <span>Gesamtpreis (brutto)</span>
                        <span>{formatPrice(getCartFullValue() * 1.19)}</span>
                      </div>
                    </div>
                    
                    {/* Deposit Section - Highlighted */}
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mt-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm font-medium text-red-800">Anzahlung (20%)</span>
                          <p className="text-xs text-red-600">Jetzt zu zahlen</p>
                        </div>
                        <span className="text-xl font-bold text-red-700">
                          {formatPrice(getCartTotal() * 1.19)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Remaining Amount */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 flex items-center">
                          <Clock size={14} className="mr-1" />
                          Restzahlung bei Lieferung
                        </span>
                        <span className="font-medium text-gray-700">
                          {formatPrice(getTotalRemainingAmount() * 1.19)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Delivery Time */}
                    <div className="flex items-center text-sm text-amber-700 mt-3 bg-amber-50 rounded-lg p-2">
                      <Truck size={16} className="mr-2" />
                      Lieferzeit: ca. 60 Tage
                    </div>
                  </>
                ) : (
                  <>
                    {/* Regular Order Summary */}
                    <div className="border-t pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Zwischensumme (netto)</span>
                        <span>{formatPrice(step === 3 && order ? order.subtotal : getCartTotal())}</span>
                      </div>

                      <div className="flex justify-between text-sm">
                        <span>MwSt. (19%)</span>
                        <span>{formatPrice(step === 3 && order ? order.tax_amount : getTaxAmount())}</span>
                      </div>

                      {/* PayPal-Ratenzahlung Aufschlag — auf Brutto, ohne MwSt */}
                      {isPayPalInstallment && step !== 3 && installmentSurchargeGross > 0 && (
                        <div className="flex justify-between text-sm text-blue-700" data-testid="paypal-surcharge-line">
                          <span>+ PayPal-Finanzierungsgebühr (10 % auf Brutto)</span>
                          <span>{formatPrice(installmentSurchargeGross)}</span>
                        </div>
                      )}

                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Versand</span>
                        <span>auf Anfrage</span>
                      </div>
                    </div>
                    
                    <div className="border-t mt-4 pt-4">
                      <div className="flex justify-between font-bold text-lg">
                        <span>Gesamt</span>
                        <span className={isPayPalInstallment && step !== 3 ? 'text-blue-700' : 'text-red-600'}>
                          {formatPrice(step === 3 && order ? order.total : (isPayPalInstallment ? displayedGrandTotal : getGrandTotal()))}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">inkl. 19% MwSt.{isPayPalInstallment && step !== 3 ? ' + PayPal-Finanzierungsgebühr' : ''}</p>
                      {isPayPalInstallment && step !== 3 && (
                        <div className="mt-3 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs leading-relaxed">
                          <p className="font-semibold text-blue-900 mb-1">Ihre Vorteile mit PayPal-Ratenzahlung:</p>
                          <ul className="text-blue-800 space-y-0.5 mb-2">
                            <li>· <strong>0 % Zinsen</strong> – Finanzierung übernimmt Jannesmann</li>
                            <li>· Bequeme Monatsraten direkt über PayPal</li>
                            <li>· Sofortige Bonitätsprüfung im PayPal-Checkout</li>
                          </ul>
                          <p className="text-blue-800 font-bold">Bei PayPal wird angezeigt: {formatPrice(displayedGrandTotal)} brutto</p>
                          <p className="mt-2 px-2 py-1.5 rounded bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-semibold">
                            ⚠ Nur für Privatkunden mit Privat-PayPal-Konto (keine PayPal-Geschäftskonten).
                          </p>
                          <p className="text-gray-600 mt-2 text-[11px]">
                            {PAYPAL_INSTALLMENT_DISCLAIMER}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Trust Elements */}
                <div className="border-t mt-4 pt-4 space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <ShieldCheck className="text-green-600 mr-2" size={16} />
                    Sichere Zahlungsabwicklung
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <FileText className="text-green-600 mr-2" size={16} />
                    PDF-Rechnung inklusive
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* AGB Warning Modal */}
      <AGBWarningModal 
        isOpen={showAGBWarning} 
        onClose={() => setShowAGBWarning(false)} 
      />
    </div>
  );
};

export default Checkout;
